// PaymentPage.js - Premium Design
import React, { useState, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useBooking } from '../hooks/useBooking';
import Layout from '../components/layout/Layout';
import ProgressSteps from '../components/common/ProgressSteps';
import PaymentForm from '../components/booking/PaymentForm';
import BookingSummary from '../components/booking/BookingSummary';
import { PAGES, BOOKING_STEPS, BOOKING_TYPES, PAYMENT_METHODS } from '../utils/constants';
import APIService from '../services/api';
import PriceCalculationService from '../services/priceCalculator';
import { formatCurrency } from '../utils/helpers';

const PaymentPage = () => {
  const {
    state: {
      searchData,
      selectedVehicle,
      extrasData = null,
      passengerData = null,
      bookingDetails = null,
      routeInfo = null
    },
    setCurrentPage,
    setBookingDetails
  } = useBooking();

  const { state: authState } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [submitRequest, setSubmitRequest] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);

  const priceResult = useMemo(() => {
    if (!selectedVehicle || !searchData?.searchFormData) return null;
    const sf = searchData.searchFormData;

    const getBookingType = () => {
      const searchType = String(sf.type || '').toLowerCase();
      const subService = String(sf.subService || '').toLowerCase();
      const isHourly = searchType.includes('hourly') || subService === 'hourly';
      if (searchType.includes('airport')) return isHourly ? BOOKING_TYPES.AIRPORT_HOURLY : BOOKING_TYPES.AIRPORT_TRANSFER;
      return isHourly ? BOOKING_TYPES.CITY_HOURLY : BOOKING_TYPES.CITY_POINT_TO_POINT;
    };

    const bookingType = getBookingType();
    const isHourly = bookingType === BOOKING_TYPES.AIRPORT_HOURLY || bookingType === BOOKING_TYPES.CITY_HOURLY;
    const pickupAtDate = sf.pickupDate && sf.pickupTime ? `${sf.pickupDate}T${sf.pickupTime}` : new Date().toISOString();
    const toNum = (v) => typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^\d.]/g, '')) || 0;
    const distanceKm = (routeInfo?.distanceKm ?? toNum(routeInfo?.distance)) || toNum(sf.distance) || 0;
    const returnMultiplier = (!isHourly && sf.showReturn) ? 2 : 1;

    return isHourly
      ? PriceCalculationService.calculateHourlyPrice(selectedVehicle, Number(sf.hours || 0), distanceKm, pickupAtDate)
      : PriceCalculationService.calculateTransferPrice(selectedVehicle, distanceKm, pickupAtDate, extrasData, returnMultiplier, sf.waitingMins || 0);
  }, [selectedVehicle, searchData, extrasData, routeInfo]);

  const totalWithExtras = priceResult?.total || 0;
  const baseAmount = (priceResult?.basePrice || priceResult?.baseFare) || 0;
  const currency = selectedVehicle?.pricing?.currency || 'USD';

  const handlePayment = async (paymentData) => {
    try {
      setProcessing(true);
      if (!passengerData) throw new Error('Passenger information missing');

      const getBookingType = () => {
        const sf = searchData.searchFormData;
        const isHourly = String(sf.type || '').toLowerCase().includes('hourly') || String(sf.subService || '').toLowerCase() === 'hourly';
        if (String(sf.type || '').toLowerCase().includes('airport')) return isHourly ? BOOKING_TYPES.AIRPORT_HOURLY : BOOKING_TYPES.AIRPORT_TRANSFER;
        return isHourly ? BOOKING_TYPES.CITY_HOURLY : BOOKING_TYPES.CITY_POINT_TO_POINT;
      };

      const bookingType = getBookingType();
      const bookingPayload = {
        bookingType, serviceType: bookingType,
        vehicleClassId: selectedVehicle.classId, vehicleClassName: selectedVehicle.className,
        basePrice: baseAmount, totalAmount: totalWithExtras, currency: selectedVehicle.pricing?.currency || 'USD',
        pax: Number(searchData.searchFormData.passengers || 1),
        bags: Number(searchData.searchFormData.luggage || searchData.searchFormData.bags || 0),
        customerName: `${passengerData.firstName} ${passengerData.lastName}`,
        customerEmail: passengerData.email, customerPhone: `${passengerData.countryCode}${passengerData.phone}`,
        pickupLocation: searchData.searchFormData.fromPlace, pickupLat: searchData.searchFormData.fromLat, pickupLng: searchData.searchFormData.fromLng,
        dropoffLocation: searchData.searchFormData.toPlace, dropoffLat: searchData.searchFormData.toLat, dropoffLng: searchData.searchFormData.toLng,
        pickupDate: searchData.searchFormData.pickupDate, pickupTime: searchData.searchFormData.pickupTime,
        flightNumber: extrasData?.flightNumber || '',
        notes: extrasData?.outwardNotes || '',
        distance: routeInfo?.distance || '',
        durationText: routeInfo?.duration || '',
        passengers: [{ firstName: passengerData.firstName, lastName: passengerData.lastName, email: passengerData.email, phone: `${passengerData.countryCode}${passengerData.phone}` }],
        extras: [
          ...(extrasData.childSeats > 0 ? [{ code: "CHILD_SEAT", name: "Child Seat", quantity: extrasData.childSeats }] : []),
          ...(extrasData.boosterSeats > 0 ? [{ code: "BOOSTER_SEAT", name: "Booster Seat", quantity: extrasData.boosterSeats }] : [])
        ]
      };

      const { token } = authState;
      const booking = await APIService.createBooking(bookingPayload, token);
      const provider = paymentData.method === PAYMENT_METHODS.WEBXPAY ? 'webxpay' : 'stripe';
      const paymentIntentPayload = {
        bookingId: booking.id, provider,
        ...(paymentData.amount !== totalWithExtras && { amount: paymentData.amount, paymentType: paymentData.paymentType }),
        ...(provider === 'webxpay' && { returnUrl: `${window.location.origin}/payment-return?bookingId=${booking.id}` })
      };

      const paymentIntent = await APIService.createPaymentIntent(paymentIntentPayload);
      setBookingDetails({
        ...paymentIntent, id: booking.id, code: booking.code, status: booking.status, magicLink: booking.magicLink,
        customerName: `${passengerData.firstName} ${passengerData.lastName}`, customerEmail: passengerData.email, customerPhone: passengerData.phone,
        pickupLocation: searchData.searchFormData.fromPlace, dropoffLocation: searchData.searchFormData.toPlace,
        pickupDate: searchData.searchFormData.pickupDate, pickupTime: searchData.searchFormData.pickupTime,
        vehicleClassName: selectedVehicle.className, passengers: searchData.searchFormData.passengers || 1,
        paymentAmount: paymentData.amount, baseAmount, extrasTotal: priceResult?.extrasTotal || 0,
        extrasData, paymentMethod: paymentData.method === PAYMENT_METHODS.WEBXPAY ? 'WebXPay' : 'Card', provider
      });
    } catch (error) {
      alert(`Setup failed: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      if (paymentIntent.paymentMethod !== 'webxpay') await APIService.confirmPayment(paymentIntent.id);
      setBookingDetails(prev => ({ ...prev, status: 'confirmed', paidAmount: prev.paymentAmount, remainingAmount: prev.totalAmount - prev.paymentAmount, paymentCompletedAt: new Date().toISOString() }));
      setTimeout(() => setCurrentPage(PAGES.CONFIRMATION), 100);
    } catch (error) {
      alert('Payment succeeded but confirmation failed. Contact support.');
    }
  };

  const handleBack = () => setCurrentPage(PAGES.PASSENGER);

  if (!searchData || !selectedVehicle) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-8 rounded-3xl text-center">
            <p className="text-gray-400 mb-4 font-medium">Session expired</p>
            <button onClick={() => setCurrentPage(PAGES.HOME)} className="bg-blue-600 text-white px-8 py-3 rounded-full font-black text-xs tracking-widest uppercase hover:bg-blue-700 transition-all shadow-lg">Start Over</button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showHeaderMobile={false}>
      <div className="min-h-screen pb-20 bg-[#050A12]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <ProgressSteps currentStep={BOOKING_STEPS.PAYMENT} />

          <div className="grid grid-cols-12 gap-8 mt-4">
            {/* Main Checkout Area */}
            <div className="col-span-12 lg:col-span-8 space-y-6">
              <div className="glass-panel p-6 md:p-10 rounded-[2.5rem] border-white/5 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-10 right-10 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
                
                <div className="flex items-center gap-4 mb-10">
                  <button onClick={handleBack} className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-all">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight italic uppercase">Checkout</h2>
                </div>

                <PaymentForm
                  selectedVehicle={selectedVehicle}
                  searchData={searchData.searchFormData}
                  extrasData={extrasData}
                  extrasTotal={priceResult?.extrasTotal || 0}
                  onPayment={handlePayment}
                  onBack={handleBack}
                  processing={processing}
                  bookingDetails={bookingDetails}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={msg => alert(msg)}
                  submitRequest={submitRequest}
                  onSubmitRequestHandled={() => setSubmitRequest(false)}
                  onCanSubmitChange={setCanSubmit}
                  routeInfo={routeInfo}
                  priceResult={priceResult}
                />
              </div>

              {/* Security Badges */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 glass-panel bg-blue-500/5 border-blue-500/10 rounded-2xl p-5 flex items-center gap-4">
                  <div className="text-2xl">🔒</div>
                  <div>
                    <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest leading-none mb-1">SSL Encrypted</p>
                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">Secure 256-bit Payment</p>
                  </div>
                </div>
                <div className="flex-1 glass-panel bg-green-500/5 border-green-500/10 rounded-2xl p-5 flex items-center gap-4">
                  <div className="text-2xl">💳</div>
                  <div>
                    <p className="text-[10px] font-black text-green-500 uppercase tracking-widest leading-none mb-1">PCI Compliant</p>
                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">Safe Card Processing</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Sidebar */}
            <div className="hidden lg:block lg:col-span-4 h-fit sticky top-24">
              <BookingSummary
                searchData={searchData.searchFormData}
                selectedVehicle={selectedVehicle}
                extrasData={extrasData}
                step={BOOKING_STEPS.PAYMENT}
                routeInfo={routeInfo}
                onContinue={() => setSubmitRequest(true)}
                isProcessing={processing}
              />
            </div>
          </div>
        </div>

        {/* Mobile Fixed Confirmation Bar */}
        {!bookingDetails?.clientSecret && (
          <div className="md:hidden fixed bottom-6 left-4 right-4 z-[100]">
            <div className="bg-[#0f172a]/95 backdrop-blur-2xl border border-white/10 rounded-full p-2 pl-6 flex items-center justify-between shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
              <div className="min-w-0">
                <p className="text-[9px] text-blue-500 font-black uppercase tracking-widest italic truncate">Due Now</p>
                <p className="text-lg font-black text-white tracking-tighter leading-none mt-0.5">
                  {formatCurrency(totalWithExtras, currency)}
                </p>
              </div>
              <button 
                onClick={() => setSubmitRequest(true)}
                disabled={!canSubmit || processing}
                className="bg-blue-600 text-white h-12 px-8 rounded-full font-black text-xs uppercase tracking-[0.15em] transition-all active:scale-95 shadow-lg shadow-blue-500/20 disabled:opacity-30"
              >
                {processing ? '...' : 'Pay Now'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentPage;